import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Trash, 
  Plus, 
  Sparkle, 
  CaretLeft, 
  CaretRight, 
  MagnifyingGlass, 
  Check, 
  Spinner
} from '@phosphor-icons/react';
import { useProposalMatcher } from '../hooks/useProposalMatcher';
import type { MatchStep } from '../hooks/useProposalMatcher';
import { useHaptic } from '../../../hooks/useHaptic';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { EmptyState } from '../../../components/ui/EmptyState';
import { FakeProgressBar } from '../../../components/ui/FakeProgressBar';

interface ProposalData {
  id?: string;
  proposal_title: string;
  domain: string;
  subdomain?: string;
  budget?: string | number;
  faculty?: string;
  department?: string;
  description?: string;
  score: number;
  cross_encoder_score?: number;
  reasoning?: string;
  keywords?: (string | null)[];
}

interface ProposalMatcherViewProps {
  onBack: () => void;
}

function getMatchCategory(score: number): { label: string; color: string; bg: string; border: string } {
  const pct = score * 100;
  if (pct >= 85) return { label: 'Excellent Match', color: 'text-emerald-700', bg: 'bg-emerald-50', border: 'border-emerald-250' };
  if (pct >= 70) return { label: 'Strong Match', color: 'text-accent-active', bg: 'bg-accent-light', border: 'border-accent/20' };
  return { label: 'Moderate Match', color: 'text-slate-600', bg: 'bg-slate-100', border: 'border-slate-200' };
}

function parseReasoningBullets(reasoning?: string): string[] {
  if (!reasoning) return [];
  const sentences = reasoning
    .split(/[.;]\s+/)
    .map(s => s.trim())
    .filter(s => s.length > 15);
  return sentences.slice(0, 4);
}

function StepBadge({ num, label, active, done }: { num: number; label: string; active: boolean; done: boolean }) {
  return (
    <div className={`flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider transition-all ${active ? 'text-accent-hover' : done ? 'text-slate-400' : 'text-slate-350'}`}>
      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-black shrink-0 transition-smooth ${
        done ? 'bg-emerald-500 text-white' : active ? 'bg-accent text-white' : 'bg-surface-hover text-secondary border border-border'
      }`}>
        {done ? <Check size={10} weight="bold" /> : num}
      </div>
      <span className="hidden sm:inline">{label}</span>
    </div>
  );
}

function StepDivider() {
  return <div className="w-6 h-px bg-border shrink-0 hidden sm:block" />;
}

function RecommendationCard({ proposal, rank, isBest }: { proposal: ProposalData; rank: number; isBest?: boolean }) {
  const { lightTap } = useHaptic();
  const [expanded, setExpanded] = useState(false);
  const cat = getMatchCategory(proposal.score);
  const bullets = parseReasoningBullets(proposal.reasoning);
  const scorePct = (proposal.score * 100).toFixed(0);
  const budgetDisplay = proposal.budget && proposal.budget !== '-' && proposal.budget !== 'nan'
    ? `₹${proposal.budget} L`
    : 'Flexible';
  const keywords = (proposal.keywords || []).filter(Boolean) as string[];

  const toggleExpand = () => {
    lightTap();
    setExpanded(!expanded);
  };

  return (
    <motion.div 
      layout
      className={`card-interactive font-sans ${
        isBest ? 'border-accent/40 shadow-md shadow-accent/10 ring-1 ring-accent/20' : ''
      }`}
    >
      <div className="px-5 pt-5 pb-4">
        <div className="flex items-start justify-between gap-4 mb-3">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            {isBest ? (
              <span className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-accent-hover bg-accent-light border border-accent/20 px-2 py-0.5 rounded-full shrink-0">
                Best Match
              </span>
            ) : (
              <span className="text-xs font-bold text-slate-400 shrink-0">#{rank}</span>
            )}
            <span className={`inline-flex items-center text-[9px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider border ${cat.bg} ${cat.color} ${cat.border} shrink-0`}>
              {cat.label}
            </span>
            <span className="text-[10px] text-slate-400 font-bold shrink-0">{scorePct}% RELEVANCE</span>
          </div>
          <div className="text-right shrink-0">
            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-1">Recommended Ask</p>
            <p className="text-sm font-extrabold text-slate-800 leading-none">{budgetDisplay}</p>
          </div>
        </div>

        <h3 className={`font-bold leading-snug mb-1 text-slate-900 ${isBest ? 'text-base' : 'text-sm'}`}>
          {proposal.id?.startsWith('P-') && (
            <span className="mr-1.5 align-middle text-[9px] font-extrabold text-slate-500 bg-slate-100 border border-slate-200 px-1.5 py-0.5 rounded-md">
              {proposal.id}
            </span>
          )}
          {proposal.proposal_title}
        </h3>
        <p className="text-[10px] text-slate-400 font-medium">
          {proposal.domain}{proposal.subdomain ? ` · ${proposal.subdomain}` : ''}{proposal.faculty ? ` · ${proposal.faculty}` : ''}
        </p>
      </div>

      {bullets.length > 0 && (
        <div className="px-5 pb-4 border-t border-border pt-4">
          <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400 mb-2.5">Why it matches</p>
          <ul className="space-y-2">
            {bullets.map((b, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-slate-600 leading-relaxed font-medium">
                <Check size={12} weight="bold" className="text-emerald-500 shrink-0 mt-0.5" />
                <span>{b}.</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="px-5 pb-4 border-t border-border pt-3">
        <button
          type="button"
          onClick={toggleExpand}
          className="flex items-center gap-1.5 text-[10px] font-bold text-slate-400 hover:text-slate-600 uppercase tracking-wider transition-colors cursor-pointer focus:outline-none"
        >
          <CaretRight size={12} weight="bold" className={`transition-transform ${expanded ? 'rotate-90' : ''}`} />
          {expanded ? 'Less Detail' : 'Project Details'}
        </button>

        <AnimatePresence>
          {expanded && (
            <motion.div 
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-3 space-y-3 overflow-hidden font-medium"
            >
              {proposal.description && (
                <p className="text-xs text-slate-655 leading-relaxed">{proposal.description}</p>
              )}
              {proposal.department && (
                <p className="text-[10px] text-slate-400">
                  <span className="font-bold text-slate-500">Department:</span> {proposal.department}
                </p>
              )}
              {keywords.length > 0 && (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {keywords.map(kw => (
                    <span key={kw} className="px-2 py-0.5 bg-slate-50 border border-slate-200 rounded-md text-[9px] font-bold text-slate-500 uppercase tracking-wide">{kw}</span>
                  ))}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

export function ProposalMatcherView({ onBack }: ProposalMatcherViewProps) {
  const { lightTap, mediumTap, error: hapticError } = useHaptic();
  
  const {
    step,
    setStep,
    searchTerm,
    setSearchTerm,
    alumniList,
    loadingAlumni,
    dropdownOpen,
    setDropdownOpen,
    selectedAlumni,
    handleSelectAlumni,
    handleStartLoading,
    researchStep,
    helperTextIdx,
    helperTexts,
    domain,
    setDomain,
    budgetText,
    setBudgetText,
    focus,
    setFocus,
    notes,
    setNotes,
    count,
    setCount,
    donorInsight,
    proposals,
    isSearching,
    sessionId,
    historySessions,
    historyOpen,
    setHistoryOpen,
    resetSession,
    loadHistorySession,
    deleteHistorySession,
    handleGenerateMatches,
  } = useProposalMatcher();

  const searchRef = useRef<HTMLDivElement>(null);

  // Same base list as the Proposals page (Proposal Fields.xlsx).
  const domains = [
    'Agriculture', 'CoE / Assets', 'Construction', 'Education', 'Energy',
    'Entrepreneurship', 'Environment', 'FarmTech', 'Healthcare', 'Heritage',
    'Inclusive Technology', 'Innovation', 'Miscellaneous', 'Road Safety',
    'School of Sustainability', 'Water',
  ];

  const stepOrder: MatchStep[] = ['selector', 'loading', 'configure', 'proposals'];
  const stepIdx = stepOrder.indexOf(step);
  const stepLabels = ['Select Donor', 'Research', 'Preferences', 'Results'];

  const handleBackClick = () => {
    lightTap();
    onBack();
  };

  const handleNewMapping = () => {
    lightTap();
    resetSession();
  };

  const handleSessionSelect = (id: string) => {
    mediumTap();
    loadHistorySession(id);
  };

  return (
    <div className="flex h-full min-h-0 font-sans">
      {/* ── Sidebar ── */}
      <AnimatePresence>
        {historyOpen && (
          <motion.div 
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: 224, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="border-r border-border flex flex-col shrink-0 h-full overflow-hidden shadow-sm"
          >
            <div className="px-4 pt-4 pb-3 flex items-center justify-between">
              <span className="text-[9px] font-bold text-slate-400 tracking-wider uppercase">Saved Mappings</span>
              <button
                onClick={() => { lightTap(); setHistoryOpen(false); }}
                className="text-slate-350 hover:text-slate-500 p-1 rounded transition-colors cursor-pointer focus:outline-none"
              >
                <CaretLeft size={14} weight="bold" />
              </button>
            </div>

            <div className="px-3 pb-3">
              <button
                onClick={handleNewMapping}
                className="w-full text-left px-3 py-2 text-xs font-bold text-slate-500 hover:text-accent hover:bg-accent-light rounded-xl flex items-center gap-1.5 transition-smooth cursor-pointer focus:outline-none"
              >
                <Plus size={14} weight="bold" />
                New Mapping
              </button>
            </div>

            <div className="h-px bg-slate-100 mx-3" />

            <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
              {historySessions.length === 0 ? (
                <div className="text-center py-8 text-[10px] text-secondary font-bold uppercase tracking-wider">No mappings yet.</div>
              ) : (
                historySessions.map(sess => (
                  <button
                    key={sess.id}
                    onClick={() => handleSessionSelect(sess.id)}
                    className={`w-full text-left px-3 py-2 rounded-xl text-xs flex items-center justify-between group transition-smooth cursor-pointer focus:outline-none ${
                      sessionId === sess.id
                        ? 'bg-accent-light text-accent-active font-bold'
                        : 'text-secondary hover:bg-surface-hover hover:text-primary'
                    }`}
                  >
                    <span className="truncate pr-1 flex-1 leading-snug font-semibold">{sess.title || 'Unnamed mapping'}</span>
                    <button
                      onClick={(e) => { hapticError(); deleteHistorySession(sess.id, e); }}
                      className="opacity-0 group-hover:opacity-100 p-0.5 text-slate-300 hover:text-rose-500 rounded transition-smooth shrink-0 cursor-pointer focus:outline-none"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Collapsed sidebar toggle */}
      {!historyOpen && (
        <button
          onClick={() => { lightTap(); setHistoryOpen(true); }}
          className="w-8 border-r border-border text-secondary hover:text-primary flex items-center justify-center transition-colors shrink-0 cursor-pointer focus:outline-none"
          title="Show Saved Mappings"
        >
          <CaretRight size={14} weight="bold" />
        </button>
      )}

      {/* ── Main Content ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between gap-4 shrink-0 font-sans shadow-xs z-10">
          <div className="flex items-center gap-3">
            <span className="p-2 bg-accent-light text-accent rounded-xl">
              <Sparkle size={20} weight="fill" />
            </span>
            <div>
              <h1 className="font-extrabold font-outfit text-xl text-slate-900 tracking-tight leading-none">Donor — Project Mapping</h1>
              <p className="text-[10px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Match the ideal research projects to a prospective donor in four steps.</p>
            </div>
          </div>

          <div className="flex items-center gap-4 shrink-0 font-sans">
            {/* Step indicator */}
            <div className="hidden md:flex items-center gap-2">
              {stepLabels.map((label, i) => (
                <div key={i} className="flex items-center gap-2">
                  <StepBadge num={i + 1} label={label} active={stepIdx === i} done={stepIdx > i} />
                  {i < stepLabels.length - 1 && <StepDivider />}
                </div>
              ))}
            </div>

            <Button
              onClick={handleBackClick}
              variant="outline"
              className="flex items-center gap-1"
            >
              <ArrowLeft size={12} weight="bold" />
              Exit
            </Button>
          </div>
        </div>

        {/* Step Content */}
        <div className="flex-1 overflow-y-auto p-6 min-h-0 bg-app">
          <AnimatePresence mode="wait">
            {/* ── STEP 1: Donor Selection ── */}
            {step === 'selector' && (
              <motion.div 
                key="selector"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="max-w-2xl mx-auto px-2 pt-8 pb-12 space-y-8"
              >
                <div>
                  <p className="text-[9px] font-bold text-accent uppercase tracking-widest mb-1.5">Step 1 of 4</p>
                  <h2 className="font-outfit font-bold text-slate-900 text-2xl tracking-tight leading-none">Select a Donor</h2>
                  <p className="text-xs text-slate-400 mt-2 font-semibold">Search and select a prospective donor from the alumni database.</p>
                </div>

                <div className="relative" ref={searchRef}>
                  <div className="relative">
                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-405">
                      <MagnifyingGlass size={18} weight="bold" />
                    </span>
                    <input
                      type="text"
                      placeholder="Search by name, organisation, batch..."
                      value={searchTerm}
                      onChange={(e) => { setSearchTerm(e.target.value); setDropdownOpen(true); }}
                      onFocus={() => setDropdownOpen(true)}
                      className="w-full bg-surface-hover border border-border focus:border-accent focus:ring-4 focus:ring-accent/10 outline-none rounded-xl pl-11 pr-4 py-3.5 text-xs text-primary transition-smooth font-medium shadow-sm"
                    />
                    {loadingAlumni && (
                      <div className="absolute right-4 top-1/2 -translate-y-1/2">
                        <Spinner size={16} className="text-accent animate-spin" />
                      </div>
                    )}
                  </div>

                  {/* Results dropdown */}
                  <AnimatePresence>
                    {dropdownOpen && (searchTerm.trim() || loadingAlumni) && (
                      <motion.div 
                        initial={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 5 }}
                        className="absolute left-0 right-0 mt-1.5 bg-surface border border-border rounded-xl shadow-card z-20 max-h-72 overflow-y-auto"
                      >
                        {loadingAlumni ? (
                          <div className="p-4 text-center text-xs text-slate-400 font-bold uppercase tracking-wider">Searching...</div>
                        ) : alumniList.length === 0 ? (
                          <div className="p-5 text-center">
                            <p className="text-sm font-semibold text-slate-700">No records found</p>
                            <p className="text-xs text-slate-400 mt-1 font-medium">Try a different name or organisation</p>
                          </div>
                        ) : (
                          <ul className="py-1">
                            {alumniList.map(alumni => {
                              const si = alumni.source_info || {};
                              const li = alumni.linkedin_info || {};
                              const lp = alumni.linkedin_profile || {};
                              const name = si.current_name || si.csv_name || li.name || lp.name || 'Alumni';
                              return (
                                <li key={alumni._id}>
                                  <button
                                    type="button"
                                    onClick={() => handleSelectAlumni(alumni)}
                                    className="w-full px-4 py-3 text-left hover:bg-surface-hover transition-colors flex items-center justify-between gap-4 cursor-pointer focus:outline-none"
                                  >
                                    <div className="min-w-0 font-medium">
                                      <p className="font-bold text-xs text-slate-800 truncate">{name}</p>
                                      <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                                        {si.designation || 'N/A'}{si.company ? ` · ${si.company}` : ''}
                                      </p>
                                    </div>
                                    {si.batch && (
                                      <span className="text-[10px] font-bold text-slate-400 shrink-0">Batch {si.batch}</span>
                                    )}
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>

                {/* Selected donor */}
                {selectedAlumni && (() => {
                  const si = selectedAlumni.source_info || {};
                  return (
                    <div className="border-t border-slate-100 pt-6 space-y-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-accent-light text-accent-hover font-black text-base flex items-center justify-center shrink-0 select-none shadow-xs">
                          {(si.current_name || si.csv_name || selectedAlumni?.linkedin_info?.name || selectedAlumni?.linkedin_profile?.name || 'D').charAt(0).toUpperCase()}
                        </div>
                        <div className="min-w-0">
                          <h3 className="font-bold text-slate-900 text-sm leading-tight">{si.current_name || si.csv_name || selectedAlumni?.linkedin_info?.name || selectedAlumni?.linkedin_profile?.name || 'Alumni'}</h3>
                          <p className="text-xs text-slate-500 font-semibold mt-1 truncate">
                            {si.designation || 'Alumni'}{si.company ? ` at ${si.company}` : ''}
                          </p>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {si.batch && <span className="text-[9px] font-bold text-accent-hover bg-accent-light border border-accent/20 px-2 py-0.5 rounded-full uppercase tracking-wider">Class of {si.batch}</span>}
                            {si.degree && <span className="text-[9px] font-bold text-slate-500 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full uppercase tracking-wider">{si.degree}</span>}
                          </div>
                        </div>
                      </div>

                      <Button
                        onClick={handleStartLoading}
                        variant="primary"
                        className="px-6 py-2.5 font-bold shadow-md hover:shadow-lg"
                      >
                        Profile & Continue
                      </Button>
                    </div>
                  );
                })()}
              </motion.div>
            )}

            {/* ── STEP 2: Loading / Web Research ── */}
            {step === 'loading' && (
              <motion.div 
                key="loading"
                initial={{ opacity: 0, scale: 0.98 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.98 }}
                className="max-w-md mx-auto pt-16 pb-12 flex flex-col items-center justify-center text-center space-y-6"
              >
                <div className="relative">
                  <Spinner size={36} className="text-accent animate-spin" />
                </div>
                
                <div className="space-y-4 w-full">
                  <div>
                    <h3 className="font-outfit font-bold text-slate-800 text-base leading-none">AI Deep Search Running</h3>
                    <p className="text-xs font-bold text-accent mt-2.5 animate-pulse min-h-[16px]">
                      {helperTexts[helperTextIdx]}
                    </p>
                  </div>

                  {/* Checklist */}
                  <div className="text-left bg-surface-hover border border-border rounded-2xl p-5 space-y-3 font-sans font-medium text-xs">
                    {[
                      'Donor profile loaded',
                      'Historical giving data analyzed',
                      'Preferences extracted',
                      'Generating donor-project recommendations'
                    ].map((labelStr, idx) => {
                      const isDone = researchStep >= idx;
                      return (
                        <div key={idx} className="flex items-center gap-2.5">
                          {isDone ? (
                            <Check size={14} weight="bold" className="text-emerald-500" />
                          ) : (
                            <div className="w-1.5 h-1.5 rounded-full bg-slate-300 animate-pulse shrink-0" />
                          )}
                          <span className={isDone ? 'text-slate-850 font-bold' : 'text-slate-400'}>{labelStr}</span>
                        </div>
                      );
                    })}
                  </div>

                  <div className="pt-2 px-2">
                    <FakeProgressBar estimatedTimeMs={180000} isComplete={step !== 'loading'} />
                  </div>
                </div>
              </motion.div>
            )}

            {/* ── STEP 3: Configure / Form ── */}
            {step === 'configure' && (
              <motion.div 
                key="configure"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="max-w-2xl mx-auto pt-6 pb-12 space-y-6"
              >
                <div>
                  <p className="text-[9px] font-bold text-accent uppercase tracking-widest mb-1.5">Step 3 of 4</p>
                  <h2 className="font-outfit font-bold text-slate-900 text-2xl tracking-tight leading-none">Configure Matching Preferences</h2>
                  <p className="text-xs text-slate-400 mt-2 font-semibold">Tweak domains, budget size, and interest focuses. AI autofilled these based on research.</p>
                </div>

                {donorInsight && (
                  <div className="bg-accent-light border border-accent/20 rounded-2xl p-4 space-y-1.5">
                    <span className="text-[9px] font-bold text-accent-hover uppercase tracking-wider">AI Donor Research Digest</span>
                    <p className="text-xs text-slate-655 leading-relaxed font-semibold">{donorInsight}</p>
                  </div>
                )}

                <form onSubmit={handleGenerateMatches} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-1.5 text-left font-sans">
                      <label className="text-[10px] font-bold text-slate-450 uppercase tracking-wider block mb-0.5">Matching Domain</label>
                      <select
                        value={domain}
                        onChange={(e) => setDomain(e.target.value)}
                        required
                        className="w-full bg-surface-hover border border-border focus:border-accent focus:ring-4 focus:ring-accent/10 outline-none rounded-xl px-3 py-2.5 text-xs text-primary transition-smooth font-medium shadow-sm"
                      >
                        <option value="">-- Select Target Domain --</option>
                        {domains.map(d => (
                          <option key={d} value={d}>{d}</option>
                        ))}
                      </select>
                    </div>

                    <Input
                      label="Recommended Ask Amount"
                      type="text"
                      placeholder="e.g. 50 Lakhs"
                      value={budgetText}
                      onChange={(e) => setBudgetText(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-450 uppercase tracking-wider block">Interest Focus (AI Autofilled)</label>
                    <textarea
                      value={focus}
                      onChange={(e) => setFocus(e.target.value)}
                      rows={3}
                      placeholder="Specific causes, departments, or research targets matching donor affinity..."
                      className="w-full bg-surface-hover border border-border focus:border-accent focus:ring-4 focus:ring-accent/10 outline-none rounded-xl px-4 py-2.5 text-xs text-primary transition-smooth font-medium placeholder-secondary shadow-sm"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-bold text-slate-450 uppercase tracking-wider block">Additional Config/Notes (Optional)</label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      rows={2}
                      placeholder="Add any specific guidelines, naming options or internal preferences..."
                      className="w-full bg-surface-hover border border-border focus:border-accent focus:ring-4 focus:ring-accent/10 outline-none rounded-xl px-4 py-2.5 text-xs text-primary transition-smooth font-medium placeholder-secondary shadow-sm"
                    />
                  </div>

                  <div className="flex justify-between items-center pt-2 font-sans font-bold">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-450 uppercase tracking-wider">Recommendations count:</span>
                      <input
                        type="number"
                        min={1}
                        max={20}
                        value={count}
                        onChange={(e) => setCount(Number(e.target.value))}
                        className="w-14 bg-white border border-slate-200 rounded-lg px-2 py-1 text-xs text-slate-800 text-center font-bold outline-none focus:border-accent focus:ring-1 focus:ring-accent/20"
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <Button
                        type="button"
                        onClick={() => setStep('selector')}
                        variant="outline"
                        className="px-4 py-2"
                      >
                        Back
                      </Button>
                      <Button
                        type="submit"
                        variant="primary"
                        className="px-5 py-2 font-bold shadow-md hover:shadow-lg"
                      >
                        Generate Matches
                      </Button>
                    </div>
                  </div>
                </form>
              </motion.div>
            )}

            {/* ── STEP 4: Matcher Results ── */}
            {step === 'proposals' && (
              <motion.div 
                key="proposals"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="max-w-4xl mx-auto space-y-6 pt-2 pb-12 font-sans font-medium"
              >
                <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-4">
                  <div>
                    <p className="text-[9px] font-bold text-accent uppercase tracking-widest mb-1.5">Step 4 of 4</p>
                    <h2 className="font-outfit font-extrabold text-slate-900 text-2xl tracking-tight leading-none">Matching Project Proposals</h2>
                    <p className="text-xs text-slate-400 mt-2 font-semibold">Matched university research campaigns ranked by donor affinity.</p>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setStep('configure')}
                      variant="outline"
                      className="px-4 py-2 font-bold"
                      disabled={isSearching}
                    >
                      Preferences
                    </Button>
                    <Button
                      onClick={handleNewMapping}
                      variant="outline"
                      className="px-4 py-2 font-bold"
                      disabled={isSearching}
                    >
                      New Match
                    </Button>
                  </div>
                </div>

                {selectedAlumni && (
                  <div className="bg-surface-hover border border-border rounded-2xl p-4 flex flex-col sm:flex-row justify-between gap-4 font-sans font-semibold text-xs text-primary shadow-sm">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-accent-light text-accent-hover font-black text-sm flex items-center justify-center shrink-0 shadow-xs">
                        {(selectedAlumni.source_info?.current_name || selectedAlumni.source_info?.csv_name || 'D').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-bold text-slate-800 text-xs">{selectedAlumni.source_info?.current_name || selectedAlumni.source_info?.csv_name || 'Alumni'}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5 truncate">{selectedAlumni.source_info?.designation || 'Alumni'} @ {selectedAlumni.source_info?.company || 'N/A'}</p>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 items-center sm:text-right text-[10px]">
                      <div>
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Target Domain</span>
                        <span className="font-bold text-slate-800 uppercase block mt-0.5">{domain}</span>
                      </div>
                      <div>
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider block">Ask Size</span>
                        <span className="font-bold text-slate-850 block mt-0.5">{budgetText}</span>
                      </div>
                    </div>
                  </div>
                )}

                {isSearching && proposals.length === 0 && (
                  <div className="py-20 text-center flex flex-col items-center justify-center gap-3 bg-surface border border-dashed border-border rounded-2xl">
                    <Spinner size={32} className="text-accent animate-spin" />
                    <p className="text-xs text-secondary font-bold uppercase tracking-wider">Semantic algorithms sorting active campaign pool...</p>
                  </div>
                )}

                {/* Proposals Grid List */}
                <div className="space-y-4">
                  {proposals.map((p, idx) => (
                    <RecommendationCard 
                      key={idx}
                      proposal={p}
                      rank={idx + 1}
                      isBest={idx === 0}
                    />
                  ))}
                </div>

                {!isSearching && proposals.length === 0 && (
                  <EmptyState
                    type="search"
                    title="No matches found"
                    description="We couldn't find any projects matching your configured preferences. Try broadening your domain or budget."
                  />
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
